import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {ListItemTrailingBadge} from '@github-ui/list-view/ListItemTrailingBadge'
import {UnsafeHTMLText} from '@github-ui/safe-html/UnsafeHTML'
import {clsx} from 'clsx'
import type React from 'react'
import {type ComponentProps, type Ref, type RefObject, useCallback, useMemo} from 'react'
import {graphql, useFragment} from 'react-relay'

import type {IssueFieldPills$key} from './__generated__/IssueFieldPills.graphql'
import type {IssuePullRequestTitle$key} from './__generated__/IssuePullRequestTitle.graphql'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import {useIssueFieldBadges} from './IssueFieldPills'
import {IssueFieldValueBadge} from './IssueFieldValueBadge'
import {IssueItemSubIssuesSummary} from './IssueItemSubIssuesSummary'
import styles from './IssuePullRequestTitle.module.css'
import {Label} from './labels/Label'
import {MetadataOverflowMenu} from './MetadataOverflowMenu'

// Inline cap for issue fields in the issues-index layout before the remainder rolls into a
// `+N more` overflow menu. Labels are never capped — they all render inline, so only fields
// (a newer addition) collapse once there are more than this many.
export const MAX_INLINE_FIELDS = 4

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const titleFragment = graphql`
  fragment IssuePullRequestTitle on IssueOrPullRequest
  @argumentDefinitions(
    labelPageSize: {type: "Int!", defaultValue: 10}
    showIssueFieldPills: {type: "Boolean!", defaultValue: false}
  ) {
    __typename
    ... on Issue {
      id
      number
      labels(first: $labelPageSize, orderBy: {field: NAME, direction: ASC}) @connection(key: "Labels_labels") {
        edges {
          node {
            ...Label
            name
            id
          }
        }
      }
      ...IssueFieldPills @dangerously_unaliased_fixme @arguments(showIssueFieldPills: $showIssueFieldPills)
    }
    ... on PullRequest {
      number
      labels(first: $labelPageSize, orderBy: {field: NAME, direction: ASC}) @connection(key: "Labels_labels") {
        edges {
          node {
            ...Label
            name
            id
          }
        }
      }
    }
  }
`

type IssuePullRequestTitleProps = {
  dataKey: IssuePullRequestTitle$key
  listItemMetadata?: IssueRowSecondaryQuery$data | null
  repositoryOwner: string
  repositoryName: string
  ref?: Ref<HTMLAnchorElement>
  href?: string
  target?: string
  onClick?: React.MouseEventHandler<HTMLElement>
  value: string
  leadingBadge?: ComponentProps<typeof ListItemTitle>['leadingBadge']
  /**
   * Href getter for the label badge links
   * @param name - name of the label
   * @returns URL to the label
   */
  getLabelHref: (name: string) => string
  /**
   * Href getter for the sub-issues progress badge links
   * @param owner - owner of the issue's repository
   * @param repo - name of the issue's repository
   * @param number - number of the issue
   * @returns URL with query for the sub-issues of the issue
   */
  getSubIssuesHref?: (owner: string, repo: string, number: number) => string
  /**
   * Href getter for the issue field value badge links.
   * @param fieldName - name of the issue field (used to build the `field.<name>:` qualifier)
   * @param queryValue - the value to put in the search query. This is the untruncated
   *   display value for text/single-select/number fields, and the raw ISO calendar
   *   date (`YYYY-MM-DD`) for date fields so the resulting search matches backend-
   *   stored values rather than the user-facing formatted string.
   * @returns URL that filters the surrounding list by the clicked field value
   */
  getIssueFieldHref?: (fieldName: string, queryValue: string) => string
  headerClassName?: string
}

export function IssuePullRequestTitle({
  dataKey,
  listItemMetadata,
  ref,
  href,
  target,
  onClick: externalOnClick,
  value,
  leadingBadge,
  getLabelHref,
  getSubIssuesHref,
  getIssueFieldHref,
  headerClassName,
  repositoryOwner,
  repositoryName,
}: IssuePullRequestTitleProps) {
  const data = useFragment(titleFragment, dataKey)

  const {number, labels} =
    data.__typename === 'PullRequest' || data.__typename === 'Issue' ? data : {number: undefined, labels: undefined}

  const issueFieldsKey = data.__typename === 'Issue' ? (data as IssueFieldPills$key) : null

  const fieldBadges = useIssueFieldBadges(issueFieldsKey)

  const onClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // shortcircuit to default link behaviors if using middle click or modifiers
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (event.button === 1 || event.shiftKey || event.ctrlKey || event.metaKey) return
      externalOnClick?.(event)
    },
    [externalOnClick],
  )

  const trailingLabels = (labels?.edges || []).flatMap(a => a?.node || [])

  // Issue fields collapse into a `+N more` overflow once there are more than MAX_INLINE_FIELDS;
  // labels are never capped and all render inline. `issueFieldsKey` is null for pull requests,
  // so `fieldBadges` is empty there and no field pills/overflow render.
  const {inlineFields, hiddenFieldCount} = splitInlineFields(fieldBadges)

  const trailingBadges: Array<React.ReactElement<typeof ListItemTrailingBadge>> = [
    ...trailingLabels.map(label => (
      <ListItemTrailingBadge key={label.id} title={label.name}>
        <Label label={label} getLabelHref={getLabelHref} fontWeight={500} />
      </ListItemTrailingBadge>
    )),
    ...inlineFields.map(badge => (
      <IssueFieldValueBadge
        key={`field-${badge.fieldName}-${badge.displayValue}`}
        fieldName={badge.fieldName}
        displayValue={badge.displayValue}
        href={getIssueFieldHref?.(badge.fieldName, badge.queryValue)}
      />
    )),
    <MetadataOverflowMenu
      key="metadata-overflow"
      hiddenFieldCount={hiddenFieldCount}
      fields={fieldBadges}
      getIssueFieldHref={getIssueFieldHref}
    />,
  ]

  if (data.__typename === 'Issue') {
    const subIssuesSummary = (
      <IssueItemSubIssuesSummary
        listItemMetadata={listItemMetadata}
        issueId={data.id}
        link={number && getSubIssuesHref ? getSubIssuesHref(repositoryOwner, repositoryName, number) : ''}
        key="sub-issues-summary"
      />
    )
    if (subIssuesSummary) trailingBadges.unshift(subIssuesSummary)
  }

  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const hovercardAttributes = useMemo(
    () =>
      number
        ? data.__typename === 'PullRequest'
          ? getHovercardAttributesForType('pull_request', {
              owner: repositoryOwner,
              repo: repositoryName,
              pullRequestNumber: number,
            })
          : data.__typename === 'Issue'
            ? getHovercardAttributesForType('issue', {
                owner: repositoryOwner,
                repo: repositoryName,
                issueNumber: number,
              })
            : {}
        : {},
    [data.__typename, getHovercardAttributesForType, number, repositoryName, repositoryOwner],
  )

  return (
    <ListItemTitle
      value={<UnsafeHTMLText as="span" html={value} />}
      onClick={onClick}
      leadingBadge={leadingBadge}
      trailingBadges={trailingBadges}
      headingClassName={clsx(headerClassName, styles.ListItemTitle_0)}
      anchorRef={ref as RefObject<HTMLAnchorElement | null>}
      anchorClassName={clsx(number && styles.ListItemTitle_1)}
      href={href}
      target={target}
      linkProps={{...hovercardAttributes, 'data-testid': 'issue-pr-title-link'}}
    />
  )
}

/**
 * Split issue field metadata into the portion rendered inline on the row and the remainder
 * that rolls into the `+N more` overflow menu. Labels are intentionally not handled here —
 * they are never capped in the issues index and always render inline.
 *
 * `fields` is empty when the row is a pull request, so this returns no inline fields and a
 * zero hidden count on that path.
 */
export function splitInlineFields<Field>(
  fields: Field[],
  maxFields = MAX_INLINE_FIELDS,
): {inlineFields: Field[]; hiddenFieldCount: number} {
  const inlineFields = fields.slice(0, maxFields)
  return {inlineFields, hiddenFieldCount: fields.length - inlineFields.length}
}
